import { Loader2, OctagonAlertIcon, Zap } from "lucide-react";

import { useTokenManagement } from "./useTokenManagement";

type CreditsHeaderProps = {
  labelHtmlFor: string;
  testIdPrefix?: string;
};

export function CreditsHeader({ labelHtmlFor, testIdPrefix = "prompt" }: CreditsHeaderProps) {
  const { aiCreditsBalance } = useTokenManagement();

  return (
    <div className="mb-1 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <label
          htmlFor={labelHtmlFor}
          className="h-full text-sm font-semibold text-gray-700"
          data-testid={`${testIdPrefix}-label`}
        >
          Your prompt
        </label>
        <div
          className={`text-sm font-bold ${aiCreditsBalance > 0 ? "text-cta-gradient" : "text-gray-400"}`}
          data-testid="credits-count"
        >
          {aiCreditsBalance}/5 remaining today
        </div>
      </div>
    </div>
  );
}

type GenerateButtonContentProps = {
  isLoading: boolean;
  hasCredits: boolean;
  actionText: string;
  loadingText?: string;
};

export function getGenerateButtonContent({
  isLoading,
  hasCredits,
  actionText,
  loadingText
}: GenerateButtonContentProps) {
  if (isLoading) {
    return (
      <>
        <Loader2 className="size-4 animate-spin" />
        {loadingText || `${actionText}...`}
      </>
    );
  }

  if (!hasCredits) {
    return (
      <>
        <Zap className="size-4 fill-white opacity-50" />
        No Credits Remaining
      </>
    );
  }

  return (
    <>
      <Zap className="size-4 fill-white" />
      {actionText}
    </>
  );
}

type ErrorMessageProps = {
  error?: string | undefined;
  showNoCreditsMessage?: boolean;
  compact?: boolean;
};

export function ErrorMessage({
  error,
  showNoCreditsMessage = false,
  compact = false
}: ErrorMessageProps) {
  if (!error && !showNoCreditsMessage) {
    return null;
  }

  const message =
    error || "You've used all your daily credits. More credits will be available tomorrow.";

  if (compact) {
    return (
      <div className="mt-1 flex items-center gap-3 rounded-lg border border-orange-200 bg-orange-50 px-2 py-1">
        <OctagonAlertIcon className="size-6 text-orange-600" />
        <p className="text-xs font-normal text-gray-700">{message}</p>
      </div>
    );
  }

  return (
    <div className="mt-2 flex items-center gap-3 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2">
      <OctagonAlertIcon className="size-6 text-orange-600" />
      <p className="text-sm font-normal leading-[18px] text-gray-700">{message}</p>
    </div>
  );
}
